import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from "../config/firebaseConfig";
import { 
  getBrandConfig, 
  detectUserCountry, 
  getCurrencyForCountry,
  getPriceConfig,
  getPlanBillingType,
  doesPlanAutoRenew,
  getMonthsForPlan,
  getPaymentProcessorForCurrency
} from '../util/domain-utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

const PricingPlans = ({ isModal = false }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activeButton, setActiveButton] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [userCountry, setUserCountry] = useState('US');
  const [currency, setCurrency] = useState(localStorage.getItem('selectedCurrency') || 'usd');
  const [plans, setPlans] = useState([]);
  const [paymentProcessor, setPaymentProcessor] = useState(getPaymentProcessorForCurrency(localStorage.getItem('selectedCurrency') || 'usd'));
  const [razorpayReady, setRazorpayReady] = useState(false);
  
  // Get brand configuration
  const brand = getBrandConfig();
  
  // MaxResumes domain name
  const domainName = 'maxresumes.com';
  
  // Load Razorpay script
  useEffect(() => {
    if (currency.toLowerCase() === 'inr') {
      const loadRazorpayScript = () => {
        return new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://checkout.razorpay.com/v1/checkout.js';
          script.async = true;
          script.onload = () => {
            setRazorpayReady(true);
            resolve();
          };
          script.onerror = reject;
          document.body.appendChild(script);
        });
      };

      // Load the script only if not already loaded
      if (!window.Razorpay) {
        loadRazorpayScript()
          .catch(err => {
            console.error('Error loading Razorpay script:', err);
            setErrorMessage('Failed to load payment processor. Please try again later.');
          });
      } else {
        setRazorpayReady(true);
      }
    }
  }, [currency]);
  
  // Detect user's country and set the appropriate currency
  useEffect(() => {
    const detectCountry = async () => {
      try {
        const storedCurrency = localStorage.getItem('selectedCurrency');
        const countryCode = await detectUserCountry();
        setUserCountry(countryCode);
        
        if (storedCurrency) {
          setCurrency(storedCurrency);
          setPaymentProcessor(getPaymentProcessorForCurrency(storedCurrency));
        } else {
          // Set currency based on country (INR for India, USD for others)
          const detectedCurrency = getCurrencyForCountry(countryCode);
          setCurrency(detectedCurrency);
          setPaymentProcessor(getPaymentProcessorForCurrency(detectedCurrency));
        }
      } catch (error) {
        console.error('Error detecting country:', error);
        // Default to USD if detection fails (global standard)
        const storedCurrency = localStorage.getItem('selectedCurrency') || 'usd';
        setCurrency(storedCurrency);
        setPaymentProcessor(getPaymentProcessorForCurrency(storedCurrency));
      }
    };
    
    detectCountry();
  }, []);

  // Handler to explicitly change currency
  const handleSetCurrency = (newCurrency) => {
    setCurrency(newCurrency);
    localStorage.setItem('selectedCurrency', newCurrency);
    setPaymentProcessor(getPaymentProcessorForCurrency(newCurrency));
  };
  
  // Generate pricing plans based on the user's currency
  useEffect(() => {
    // Define plan features based on billing type
    const getFeatures = (planId) => {
      const isAutoRenew = doesPlanAutoRenew(planId, currency);
      
      const commonFeatures = [
        'Unlimited resumes and cover letters',
        'All premium templates and colors',
        'Unlimited PDF downloads',
        'Unlimited AI generations',
        'No watermarks on resumes',
        'AI-powered resume optimization',
      ];
      
      // Plan-specific features
      const planSpecificFeatures = {
        daily: [
          isAutoRenew 
            ? 'Daily billing, cancel anytime' 
            : 'One-time payment for 24 hours',
          'Perfect for quick projects'
        ],
        sevenDays: [
          'One-time payment for 7 days',
          'No auto-renewal'
        ],
        oneMonth: [
          isAutoRenew 
            ? 'Monthly billing, cancel anytime' 
            : 'One-time payment for 1 month',
          'Job tracking features',
          '7-day money back guarantee'
        ],
        threeMonth: [
          isAutoRenew 
            ? 'Billed every 3 months, cancel anytime' 
            : 'One-time payment for 3 months',
          'Job tracking features',
          '7-day money back guarantee'
        ],
        sixMonth: [
          isAutoRenew 
            ? 'Billed every 6 months, cancel anytime' 
            : 'One-time payment for 6 months',
          'Job tracking features',
          '7-day money back guarantee'
        ],
        yearly: [
          isAutoRenew 
            ? 'Annual billing, renews yearly' 
            : 'One-time payment for 12 months',
          'Job tracking features',
          'Email templates for job applications',
          '7-day money back guarantee'
        ],
        lifetime: [
          'Lifetime access — pay once, keep forever',
          'All future premium templates included',
          'Unlimited PDF downloads forever',
          'Job tracking & cover letter features',
          'Priority customer support',
          '7-day money back guarantee'
        ]
      };
      
      return [...commonFeatures, ...planSpecificFeatures[planId]];
    };
    
    // Generate plans with correct currency pricing and billing type
    const updatedPlans = [
      {
        id: 'daily',
        name: '1 Day',
        price: getPriceConfig('daily', currency).formatted,
        period: getPriceConfig('daily', currency).period,
        features: getFeatures('daily'),
        autoRenews: doesPlanAutoRenew('daily', currency),
        buttonText: `Upgrade Now`,
        hidden: true
      },
      {
        id: 'sevenDays',
        name: '7 Days',
        price: getPriceConfig('sevenDays', currency).formatted,
        period: getPriceConfig('sevenDays', currency).period,
        features: getFeatures('sevenDays'),
        autoRenews: doesPlanAutoRenew('sevenDays', currency),
        buttonText: `Upgrade Now`,
        hidden: false
      },
      {
        id: 'oneMonth',
        name: '1 Month',
        price: getPriceConfig('oneMonth', currency).formatted,
        period: getPriceConfig('oneMonth', currency).period,
        features: getFeatures('oneMonth'),
        autoRenews: doesPlanAutoRenew('oneMonth', currency),
        buttonText: `Upgrade Now`,
        hidden: false
      },
      {
        id: 'threeMonth',
        name: '3 Months',
        price: getPriceConfig('threeMonth', currency).formatted,
        period: getPriceConfig('threeMonth', currency).period,
        features: getFeatures('threeMonth'),
        autoRenews: doesPlanAutoRenew('threeMonth', currency),
        recommended: false,
        savings: '21%',
        buttonText: `Upgrade Now`,
        hidden: true
      },
      {
        id: 'sixMonth',
        name: '6 Months',
        price: getPriceConfig('sixMonth', currency).formatted,
        period: getPriceConfig('sixMonth', currency).period,
        features: getFeatures('sixMonth'),
        autoRenews: doesPlanAutoRenew('sixMonth', currency),
        savings: '25%',
        buttonText: `Upgrade Now`,
        hidden: true
      },
      {
        id: 'yearly',
        name: '1 Year',
        price: getPriceConfig('yearly', currency).formatted,
        period: getPriceConfig('yearly', currency).period,
        features: getFeatures('yearly'),
        autoRenews: doesPlanAutoRenew('yearly', currency),
        savings: '55%',
        buttonText: `Upgrade Now`,
        hidden: true
      },
      {
        id: 'lifetime',
        name: 'Life Time Access',
        price: getPriceConfig('lifetime', currency).formatted,
        period: getPriceConfig('lifetime', currency).period,
        features: getFeatures('lifetime'),
        autoRenews: false,
        recommended: true,
        badge: 'BEST VALUE',
        savings: 'Best Value',
        buttonText: `Get Lifetime Access`,
        hidden: false
      }
    ];
    
    setPlans(updatedPlans);
    
    // Update payment processor when currency changes
    setPaymentProcessor(getPaymentProcessorForCurrency(currency));
  }, [currency]);

   // Toggle currency
  const toggleCurrency = () => {
    setCurrency(prevCurrency => prevCurrency === 'inr' ? 'usd' : 'inr');
  };
  
  // Razorpay checkout handler for INR payments
  const handleRazorpayCheckout = async (planId) => {
    try {
      setLoading(true);
      setActiveButton(planId);
      setErrorMessage('');
      
      // If the user is not logged in, redirect to sign-up/login
      if (!user || !user.email) {
        // Store the selected plan and currency in localStorage to redirect back after login
        localStorage.setItem('selectedPlan', planId);
        localStorage.setItem('selectedCurrency', currency);
        navigate('/login?redirect=plans');
        return;
      }

      if (!razorpayReady) {
        setLoading(false);
        setActiveButton(null);
        return;
      }
      
      const userId = user.uid;
      
      // Create a Razorpay order with MaxResumes domain
      const response = await axios.post(`${BACKEND_URL}/api/create-razorpay-order`, {
        planId,
        userId,
        email: user.email,
        name: user.displayName || user.email,
        domainName: domainName // Include MaxResumes domain
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to create Razorpay order');
      }

      const { order, key_id, user: userData, config } = response.data;

      // Initialize Razorpay options
      const options = {
        key: key_id,
        amount: order.amount,
        currency: order.currency,
        name: config.display.name,
        description: config.display.description,
        image: config.display.logo,
        order_id: order.id,
        handler: async function(response) {
          try {
            // Process the payment verification with MaxResumes domain
            const verifyResponse = await axios.post(`${BACKEND_URL}/api/verify-razorpay-payment`, {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
              userId,
              planId,
              email: user.email,
              domainName: domainName // Include MaxResumes domain
            });

            if (verifyResponse.data.success) {
              // Redirect to success page
              navigate(`/success?payment_id=${response.razorpay_payment_id}`);
            } else {
              throw new Error(verifyResponse.data.message || 'Payment verification failed');
            }
          } catch (error) {
            console.error('Error verifying payment:', error);
            setErrorMessage(error.message || 'Payment verification failed');
          } finally {
            setLoading(false);
            setActiveButton(null);
          }
        },
        prefill: {
          name: userData.name,
          email: userData.email,
          contact: userData.contact || ''
        },
        notes: {
          userId: userId,
          planId: planId,
          domainName: domainName // Include MaxResumes domain in notes
        },
        theme: {
          color: config.theme.color
        },
        modal: {
          ondismiss: function() {
            setLoading(false);
            setActiveButton(null);
            console.log('Razorpay Checkout dismissed');
          }
        }
      };

      // Open Razorpay checkout
      const razorpay = new window.Razorpay(options);
      razorpay.open();

      // Handle Razorpay errors
      razorpay.on('payment.failed', function(response) {
        const error = response.error || {};
        setErrorMessage(error.description || 'Payment failed');
        setLoading(false);
        setActiveButton(null);
      });
      
    } catch (error) {
      console.error('Error with Razorpay checkout:', error);
      const errMsg = error.response?.data?.message || error.message || 'There was an error setting up your payment. Please try again.';
      setErrorMessage(errMsg);
      setLoading(false);
      setActiveButton(null);
    }
  };

  // Stripe checkout handler for USD payments
  const handleStripeCheckout = async (planId) => {
    try {
      setLoading(true);
      setActiveButton(planId);
      setErrorMessage('');
      
      // If the user is not logged in, redirect to sign-up/login
      if (!user || !user.email) {
        // Store the selected plan and currency in localStorage to redirect back after login
        localStorage.setItem('selectedPlan', planId);
        localStorage.setItem('selectedCurrency', currency);
        navigate('/login?redirect=plans');
        return;
      }
      
      const userId = user.uid;
      
      // Check if user already has a Stripe customer ID
      let customerId;
      const userUidDocRef = doc(db, 'users', user.uid);
      const userEmailDocRef = doc(db, 'users', user.email);
      
      try {
        const [uidDoc, emailDoc] = await Promise.all([
          getDoc(userUidDocRef).catch(() => null),
          getDoc(userEmailDocRef).catch(() => null)
        ]);
        
        if (uidDoc && uidDoc.exists() && uidDoc.data().customerId) {
          customerId = uidDoc.data().customerId;
        } else if (emailDoc && emailDoc.exists() && emailDoc.data().customerId) {
          customerId = emailDoc.data().customerId;
        }
      } catch (docErr) {
        console.warn('Could not read existing customer doc:', docErr);
      }
      
      if (!customerId) {
        // Create a new Stripe customer
        const createCustomerResponse = await axios.post(`${BACKEND_URL}/api/create-customer`, {
          email: user.email,
          name: user.displayName || user.email,
          userId: userId
        });
        
        if (createCustomerResponse.data.success) {
          customerId = createCustomerResponse.data.customerId;
          
          // Store customer ID in Firestore with userId under both documents
          const custData = {
            email: user.email,
            customerId,
            userId,
            createdAt: new Date()
          };
          await Promise.all([
            setDoc(userUidDocRef, custData, { merge: true }).catch(() => null),
            setDoc(userEmailDocRef, custData, { merge: true }).catch(() => null)
          ]);
        } else {
          throw new Error(createCustomerResponse.data.message || 'Failed to create customer');
        }
      }
      
      // Create a checkout session with userId in metadata
      const response = await axios.post(`${BACKEND_URL}/api/create-checkout-session`, {
        planId,
        customerId,
        currency: currency,
        email: user.email,
        userId: userId
      });
      
      if (response.data.success) {
        // Store pending subscription info with userId
        const pendingData = {
          pendingSubscription: true,
          pendingPlanType: planId,
          userId: userId,
          pendingSessionId: response.data.sessionId,
          updatedAt: new Date()
        };
        await Promise.all([
          setDoc(userUidDocRef, pendingData, { merge: true }).catch(() => null),
          setDoc(userEmailDocRef, pendingData, { merge: true }).catch(() => null)
        ]);
        
        // Redirect to Stripe checkout
        window.location.href = response.data.url;
      } else {
        throw new Error(response.data.message || 'Failed to create checkout session');
      }
    } catch (error) {
      console.error('Error selecting plan:', error);
      const errMsg = error.response?.data?.message || error.message || 'There was an error processing your request. Please try again.';
      setErrorMessage(errMsg);
      setLoading(false);
      setActiveButton(null);
    }
  };

  // Handle plan selection based on payment processor
  const handleSelectPlan = (planId) => {
    if (paymentProcessor === 'razorpay') {
      handleRazorpayCheckout(planId);
    } else {
      handleStripeCheckout(planId);
    }
  };
  
  // ✅ FIXED: Handle returning users with stored plan selection more carefully
  useEffect(() => {
    const checkStoredPlan = async () => {
      const storedPlan = localStorage.getItem('selectedPlan');
      const storedCurrency = localStorage.getItem('selectedCurrency');
      
      // Only proceed if user is logged in AND came from login redirect
      if (storedPlan && user && user.email) {
        // Check if we came from login redirect
        const urlParams = new URLSearchParams(window.location.search);
        const redirectParam = urlParams.get('redirect');
        
        // Only auto-trigger if coming from login redirect
        if (redirectParam === 'plans' || window.location.search.includes('redirect=plans')) {
          console.log('Auto-triggering checkout for returning user after login');
          
          // Clear the stored plan first to prevent loops
          localStorage.removeItem('selectedPlan');
          localStorage.removeItem('selectedCurrency');
          
          // Use stored currency if available, otherwise use detected currency
          if (storedCurrency) {
            setCurrency(storedCurrency);
          }
          
          // Wait a bit for currency to settle and processors to be ready
          setTimeout(() => {
            handleSelectPlan(storedPlan);
          }, 1000);
        } else {
          // User is on plans page but didn't come from login redirect
          // Clear any stored plans to prevent auto-trigger
          console.log('Clearing stored plan - user navigated to plans directly');
          localStorage.removeItem('selectedPlan');
          localStorage.removeItem('selectedCurrency');
        }
      }
    };
    
    checkStoredPlan();
  }, [user]);

  // ✅ ADDED: Clear stored plans when component unmounts or page unloads
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Clear stored plans when leaving the page (except for login redirect)
      const urlParams = new URLSearchParams(window.location.search);
      const redirectParam = urlParams.get('redirect');
      
      if (!redirectParam) {
        localStorage.removeItem('selectedPlan');
        localStorage.removeItem('selectedCurrency');
      }
    };

    // Add event listener for page unload
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup function
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      
      // Also clear on component unmount if not going to login
      const currentPath = window.location.pathname;
      if (!currentPath.includes('/login')) {
        localStorage.removeItem('selectedPlan');
        localStorage.removeItem('selectedCurrency');
      }
    };
  }, []);

  return (
    <div className={isModal ? "w-full px-1 py-1" : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4"}>   
      {errorMessage && (
        <div className="mb-4 p-3 bg-red-50 border-l-4 border-red-500 text-red-700 rounded text-sm">
          <p className="font-medium">{errorMessage}</p>
        </div>
      )}      
      
      {/* Currency toggle */}
      <div className={`flex flex-col items-center justify-center ${isModal ? 'mb-4' : 'mb-8'}`}>
        <div className="inline-flex p-1 rounded-xl bg-gray-100 border border-gray-200 shadow-sm">
          <button
            type="button"
            onClick={() => handleSetCurrency('usd')}
            className={`flex items-center space-x-2 ${isModal ? 'px-3 py-1.5 text-xs' : 'px-5 py-2 text-sm'} rounded-lg font-semibold transition-all duration-200 ${
              currency.toLowerCase() === 'usd'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <span>🇺🇸</span>
            <span>US Dollar ($)</span>
          </button>
          <button
            type="button"
            onClick={() => handleSetCurrency('inr')}
            className={`flex items-center space-x-2 ${isModal ? 'px-3 py-1.5 text-xs' : 'px-5 py-2 text-sm'} rounded-lg font-semibold transition-all duration-200 ${
              currency.toLowerCase() === 'inr'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <span>🇮🇳</span>
            <span>Indian Rupee (₹)</span>
          </button>
        </div>
        <p className="mt-1.5 text-[11px] text-gray-500">
          {currency.toLowerCase() === 'inr' ? 'Payments processed via Razorpay (UPI, Cards, Netbanking)' : 'Global payments processed via Stripe (Cards, Apple Pay, Google Pay)'}
        </p>
      </div>

      <div className={`flex flex-wrap justify-center gap-3 md:gap-4 ${isModal ? 'py-1' : 'py-4 overflow-x-auto'}`}>
        {plans.filter(plan => !plan.hidden).map((plan) => (
          <div 
            key={plan.id} 
            className={`w-full sm:w-[calc(50%-0.5rem)] ${isModal ? 'md:w-64 lg:w-72' : 'md:w-80'} flex-shrink-0 bg-white relative rounded-2xl overflow-hidden transition-all duration-300 mb-4 border-2 ${
              plan.id === 'lifetime'
                ? 'border-indigo-400 shadow-md'
                : plan.recommended
                  ? 'border-blue-400 shadow-sm'
                  : 'border-gray-200'
            } hover:shadow-lg flex flex-col justify-between`}
          >
            {plan.recommended && (
              <div className="absolute text-center rounded-full text-xs p-2 w-20 h-20 flex items-center justify-center -top-3.5 -right-2.5 bg-yellow-200 text-red-800 font-semibold shadow-sm">
                {plan.badge || 'MOST POPULAR'}
              </div>
            )}            
            <div className={`${isModal ? 'p-5 py-6' : 'p-6 py-10'} bg-white flex flex-col h-full justify-between`}> 
              <div>
                <div className="text-center mb-4">
                  <div className="flex justify-center items-baseline">
                    <span className={`${isModal ? 'text-2xl' : 'text-3xl'} py-1 font-bold text-gray-900`}>{plan.price}</span>
                  </div>
                  
                  <div className="flex items-center justify-center space-x-2 mt-1">
                    <h3 className="text-base font-semibold text-gray-600">{plan.name}</h3>
                    {plan.savings && (
                      <div className="inline-block bg-yellow-100 text-red-700 text-[11px] font-semibold px-2 py-0.5 rounded">
                        {plan.savings.includes('%') ? `Save ${plan.savings}` : plan.savings}
                      </div>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-center text-gray-500">
                    {plan.autoRenews 
                      ? 'Auto-renews at period end. Cancel anytime.'
                      : 'One-time payment. No auto-renewal.'}
                  </p>
                </div>

                <button
                  onClick={() => handleSelectPlan(plan.id)}
                  disabled={loading && activeButton === plan.id}
                  className={`w-full flex justify-center items-center px-4 py-2.5 rounded-lg text-white font-medium transition-colors ${
                    plan.id === 'lifetime'
                      ? 'bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 shadow-md'
                      : 'btn-theme plan-btn'
                  } focus:outline-none focus:ring-2 focus:ring-offset-2 text-sm`}
                >
                  {loading && activeButton === plan.id ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </>
                  ) : (
                    plan.buttonText
                  )}
                </button>

                <div className={`space-y-1.5 ${isModal ? 'mt-4' : 'mt-6'}`}>
                  {plan.features.map((feature, index) => (
                    <div key={index} className="flex items-start">
                      <svg className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                      <span className={`${isModal ? 'text-[13px]' : 'text-[15px]'} text-gray-700`}>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {!isModal ? (
        <div className="mt-12 text-center">
          <p className="text-sm text-gray-500">
            All plans include secure payment processing and data encryption.
            <br />
            Questions? <a href="mailto:support@maxresumes.com" className="text-blue-600 hover:text-blue-800 font-medium">Contact our support team</a>.
          </p>
        </div>
      ) : (
        <div className="mt-2 text-center">
          <p className="text-xs text-gray-400">
            🔒 256-bit encrypted checkout · Instant template download · 7-day money back guarantee
          </p>
        </div>
      )}
    </div>
  );
};

export default PricingPlans;